import api from './ky';

export interface FeedbackRequest {
  surveyQuestions: string;
  fullTranscript: string;
  version?: number;
}

export interface FeedbackItem {
  questionRef: string;
  customerResponse: string;
  problem: string;
  actionRequired: string;
  priority: 'high' | 'medium' | 'low';
}

export interface FeedbackResponse {
  feedbackItems: FeedbackItem[];
}

// Default survey questions for PS surveys
export const DEFAULT_SURVEY_QUESTIONS = `
Q1: Have you received a set of documents that you signed at the point of sale? This could be either email or paper copy.
Q2: What language did your financial consultant use during point of sale?
Q3: Before recommending this product, did your financial consultant ask you questions to understand your needs, financial situation, investment objectives and risk appetite?
Q4: Was the information provided by you about your financial situation and financial needs documented accurately in the sales documents by your financial consultant?
Q5: Did you understand your financial consultant's explanation on how the recommended policy is suitable for you?
Q6: In making the product recommendation to you, did your financial consultant explain the features and benefits?
Q7: Did your financial consultant explain the fees and charges?
Q8: Did your financial consultant explain the terms of the policy?
Q9: Did your financial consultant explain the investment risk?
Q10: Did your financial consultant inform you about 14-day free look period?
Q11: Was the product purchased meant to replace any of your existing policy(ies)?
Q12: Were there any aspects of the sales process or the policy that you would like to provide feedback on for our improvement?
`.trim();

// V5 Korean outbound call evaluation criteria (EC1-EC11)
export const V5_EVALUATION_CRITERIA = `
EC1: 본인 확인 — 통화 시작 시 고객(이상호) 본인 여부를 확인했는가?
EC2: 자기소개 — 상담사 이름(이지선), 소속(휴먼생명보험), 담당 어드바이저(김동민) 이름을 모두 밝혔는가?
EC3: 녹음 고지 — 통화 품질 관리를 위한 녹음 사실을 고지했는가?
EC4: 통화 목적 설명 — 보장 내용 점검 목적임을 명확히 전달했는가?
EC5: 상담 일정 제안 — 어드바이저 김동민과의 상담 일정을 제안했는가?
EC6: 거부/이의제기 처리 — 고객의 거부나 이의제기에 공감하며 적절히 대응했는가?
EC7: 시간 확인 — 상담 가능 시간을 월~금 09:00~18:00 범위 내에서 확인했는가?
EC8: 스크립트 준수 — 11단계 통화 흐름을 순서대로 따랐는가?
EC9: 존댓말 사용 — 통화 전반에 걸쳐 적절한 존댓말(높임말)을 사용했는가?
EC10: 통화 종료 — 자연스러운 마무리 인사와 함께 통화를 종료했는가?
EC11: 전반적 전문성 — 전문적이면서도 따뜻한 어조를 유지했는가?
`.trim();

// V6 Japanese outbound call evaluation criteria (EC1-EC11)
export const V6_EVALUATION_CRITERIA = `
EC1: 本人確認 — 通話開始時に顧客（田中浩）本人であることを確認したか？
EC2: 自己紹介 — アシスタント名（ケンジ）、所属（日本損保）、担当アドバイザー名（鈴木大輝）をすべて明示したか？
EC3: 通話目的の説明 — 保障内容の点検目的であることを明確に伝えたか？
EC4: 相談日程の提案 — アドバイザー鈴木大輝との相談日程を提案したか？
EC5: 拒否・異議への対応 — 顧客の拒否や異議に共感しながら適切に対応したか？
EC6: 時間確認 — 相談可能時間を月〜金09:00〜18:00の範囲内で確認したか？
EC7: スクリプト遵守 — 11段階の通話フローを順番通りに従ったか？
EC8: 敬語使用 — 通話全般にわたり適切な敬語（丁寧語・尊敬語）を使用したか？
EC9: 一ステップ一ターン — 一度に一つのステップのみ進行し、顧客の応答を待ったか？
EC10: 通話終了 — 自然な締めの挨拶とともに通話を終了したか？
EC11: 全般的な専門性 — プロフェッショナルでありながら温かみのある語調を維持したか？
`.trim();

// V7 Traditional-Chinese evaluation criteria — New-Contract Pre-Underwriting Verification Call (CS-155WI)
// The Traditional-Chinese wording also forces the AI analysis output into Traditional Chinese.
export const V7_EVALUATION_CRITERIA = `
【語言要求 — 最高優先，務必嚴格遵守】所有輸出內容一律使用「台灣繁體中文」，嚴禁出現任何簡體字；questionRef、problem、customerResponse、actionRequired 等所有欄位皆必須為繁體中文。即使逐字稿（transcript）中出現簡體字，輸出時也必須一律改寫為台灣繁體中文。數字與金額以新台幣及台灣用語表示。再次強調：禁止任何簡體字，全部使用台灣繁體中文。
以下為「新契約承保前電話訪問」評核項目：
EC1: 本人確認 — 通話開始時是否確認為客戶（王先生）本人？
EC2: 自我介紹與錄音告知 — 是否表明為保誠人壽電話訪問員、招攬通路與服務專員，並告知全程錄音且取得同意？
EC3: 身分驗證 — 是否以身分證字號後三碼或出生年月日完成身分確認？
EC4: 親簽確認 — 是否確認要保人／被保險人／繳費人本人親自簽名？
EC5: 商品適合度 — 是否確認服務專員已清楚說明保單內容且客戶充分了解？
EC6: 商品特性與風險告知 — 是否說明匯率風險、投資型不保證本金等對應商品風險，並確認客戶了解？
EC7: 保費來源 — 是否確認保費是否來自解約、貸款或保單借款，並善盡保單權益提醒？
EC8: 保費負擔 — 是否確認保費是否影響客戶家庭經濟或必要生活費用？
EC9: 契約撤銷權 — 是否告知自收到保單次日起10日的契約撤銷權？
EC10: 高齡關懷 — 面對65歲以上客戶是否放慢語速、確認理解並留意金融剝削？
EC11: 結束與專業度 — 是否禮貌結束、提供客服專線，並全程保持專業親切的語氣？
`.trim();

// V8 Traditional-Chinese evaluation criteria — Renewal Payer 65+ Premium-Deduction Confirmation
export const V8_EVALUATION_CRITERIA = `
【語言要求 — 最高優先，務必嚴格遵守】所有輸出內容一律使用「台灣繁體中文」，嚴禁出現任何簡體字；questionRef、problem、customerResponse、actionRequired 等所有欄位皆必須為繁體中文。即使逐字稿（transcript）中出現簡體字，輸出時也必須一律改寫為台灣繁體中文。數字與金額以新台幣及台灣用語表示。再次強調：禁止任何簡體字，全部使用台灣繁體中文。
以下為「續期保費授權扣款確認」評核項目：
EC1: 本人確認 — 通話開始時是否確認為客戶（王先生）本人？
EC2: 自我介紹與錄音告知 — 是否表明為保誠人壽客服訪問員，說明授權扣款確認目的並告知全程錄音？
EC3: 身分驗證 — 是否至少核對兩項身分資訊（身分證前三碼／出生年／居住縣市區／繳費方式）？
EC4: 親簽確認 — 是否確認授權扣款申請書由本人親自簽名（約定扣款或信用卡）？
EC5: 扣款資訊確認 — 是否確認扣款銀行帳號，或信用卡發卡行、卡別、卡號、有效期限等資訊？
EC6: 保費金額確認 — 是否確認本期應繳續期保費金額並取得客戶同意？
EC7: 高齡溝通 — 是否放慢語速、口齒清晰、必要時重複，符合對長者的溝通方式？
EC8: 結束與專業度 — 是否禮貌結束並全程保持專業親切的語氣？
`.trim();

export const generateSurveyFeedback = async (
  surveyQuestions: string,
  fullTranscript: string,
  version?: number,
): Promise<FeedbackResponse> => {
  try {
    const response = await api
      .post('api/ps/survey/feedback', {
        json: {
          surveyQuestions,
          fullTranscript,
          version,
        },
        timeout: 15000, // 15 second timeout since analysis can take time
      })
      .json<FeedbackResponse>();

    return response;
  } catch (error: any) {
    console.error('Failed to generate PS survey feedback:', error);

    // Provide more specific error messages based on ky's error structure
    if (error.response) {
      const status = error.response.status;
      if (status === 400) {
        throw new Error(
          'Invalid input format. Please check survey questions and transcript.',
        );
      } else if (status === 500) {
        throw new Error('Analysis failed. Please try again later.');
      } else if (status === 404) {
        throw new Error(
          'PS Survey Feedback API endpoint not found. Please check your server configuration.',
        );
      }
    } else if (error.name === 'TimeoutError') {
      throw new Error('Request timeout. The analysis is taking too long.');
    } else if (!error.response) {
      throw new Error(
        'Unable to connect to the server. Please check your connection.',
      );
    }

    throw new Error('Failed to generate feedback analysis.');
  }
};

// Helper function to convert transcript array to string format
export const formatTranscriptForAPI = (
  transcript: Array<{ source: string; message: string; timestamp: Date }>,
): string => {
  return transcript
    .map((entry) => {
      const role = entry.source === 'ai' ? 'Agent' : 'Customer';
      return `${role}: ${entry.message}`;
    })
    .join('\n');
};

// Helper function to get priority color for UI
export const getPriorityColor = (
  priority: string,
): 'error' | 'warning' | 'info' => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'info';
    default:
      return 'info';
  }
};

// Health check function to test if PS API is available
export const checkPSAPIHealth = async (): Promise<{
  status: string;
  service: string;
}> => {
  try {
    const response = await api
      .get('api/ps/health')
      .json<{ status: string; service: string }>();
    return response;
  } catch (error) {
    console.error('PS API health check failed:', error);
    throw new Error('PS Survey Feedback API is not available');
  }
};
